import { useCallback, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useDebouncedCallback } from "use-debounce";

/**
 * Returns common properties and functions for a data source pagination
 *
 * @param {function} dataService to execute
 */

const locaDefaultOrder = {
  order: "asc",
  orderBy: "",
};

const defaultFilter = {
  keyword: "",
};

const initialRequestState = {
  isPending: false,
  isDone: false,
  isError: false,
};

export const useFilterPagination = (
  dataService,
  size,
  itemKeyId,
  initialFilter = defaultFilter,
  parameterId,
  defaultOrder = locaDefaultOrder
) => {
  const [filter, setFilter] = useState({
    ...initialFilter,
  });
  const [order, setOrder] = useState(defaultOrder.order);
  const [orderBy, setOrderBy] = useState(defaultOrder.orderBy);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [sourceData, setSourceData] = useState([]);
  const [totalDataRecords, setTotalDataRecords] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(size);
  const [currentTotalPages, setCurrentTotalPages] = useState(0);
  const [error, setError] = useState({ isError: false, message: "" });
  const [request, setRequest] = useState(initialRequestState);

  const handleResetData = () => {
    setSourceData([]);
    setTotalDataRecords(0);
    setCurrentPageNumber(1);
    setCurrentTotalPages(0);
    setHasNextPage(true);
  };

  const resetError = useCallback(() => {
    setError({ isError: false, message: "" });
  }, [setError]);

  const handleEndOfLine = () => {
    setHasNextPage(false);
    setCurrentPageNumber(1);
    setCurrentTotalPages(0);
  };

  const handleLoadData = useCallback(async () => {
    setRequest({
      ...initialRequestState,
      isPending: true,
    });
    try {
      setIsFetching(true);
      const response = await dataService({
        ...filter,
        sortOrder: order,
        orderBy,
        pageNumber: currentPageNumber,
        pageSize: currentPageSize,
        id: parameterId,
      });

      if (response.status === 200) {
        const {
          data: { totalRecords, data },
        } = response.data;

        if (data.length > 0) {
          setHasNextPage(true);
          setSourceData([...sourceData, ...data]);
          setCurrentPageNumber(currentPageNumber + 1);
          setCurrentTotalPages(Math.floor(totalRecords / size));
        } else {
          handleEndOfLine();
        }

        setTotalDataRecords(totalRecords);
      }
    } catch (e) {
      const errorResponse = e.response;
      let message = "default message";

      if (errorResponse?.status === 500) {
        message = errorResponse.statusText || message;
      } else {
        message = e.response?.data?.error
          ? e.response.data.error
          : "default message";
      }
      handleEndOfLine();
      setError({ isError: true, message });
      setRequest({ ...request, isError: true });
    } finally {
      setIsFetching(false);
      setRequest({ ...request, isDone: true, isPending: false });
    }
  }, [
    dataService,
    currentPageNumber,
    currentPageSize,
    filter,
    parameterId,
    sourceData,
    size,
    order,
    orderBy,
    request,
  ]);

  const debouncedHandleFilterChange = useDebouncedCallback((value, name) => {
    setFilter({ ...filter, [name]: value });
    handleResetData();
  }, 1000);

  const debouncedHandleFiltersChange = useDebouncedCallback((filters) => {
    setFilter({ ...filter, ...filters });
    handleResetData();
  }, 1000);

  const debouncedHandleSortChange = useDebouncedCallback((field, orderData) => {
    setOrderBy(field);
    setOrder(orderData);
    handleResetData();
  }, 1000);

  const infiniteRef = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: handleLoadData,
  });

  const removeItemFromData = useCallback(
    (itemId = null) => {
      setSourceData((prevSourceData) => {
        const newSourceData = prevSourceData.filter(
          (data) => data[itemKeyId] !== itemId
        );
        const shouldUpdateTotalDataRecords =
          newSourceData.length !== prevSourceData.length;
        if (shouldUpdateTotalDataRecords) {
          setTotalDataRecords(totalDataRecords - 1);
        }

        return newSourceData;
      });
    },
    [itemKeyId, totalDataRecords]
  );

  const updateItemFromData = useCallback(
    (updatedItem) => {
      setSourceData((prevSourceData) =>
        prevSourceData.map((data) =>
          data[itemKeyId] === updatedItem[itemKeyId] ? { ...updatedItem } : data
        )
      );
    },
    [itemKeyId]
  );

  return {
    debouncedHandleFilterChange: debouncedHandleFilterChange.callback,
    debouncedHandleFiltersChange: debouncedHandleFiltersChange.callback,
    debouncedHandleSortChange: debouncedHandleSortChange.callback,
    handleResetData,
    setCurrentPageSize,
    setError,
    resetError,
    request,
    infiniteRef,
    isFetching,
    sourceData,
    totalDataRecords,
    currentPageSize,
    currentTotalPages,
    removeItemFromData,
    error,
    order,
    orderBy,
    updateItemFromData,
  };
};
